import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const ChangePassword = ({ setAuth, setUserType }) => {
  const [formData, setFormData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [loading, setLoading] = useState(false);
  const [isFirstLogin, setIsFirstLogin] = useState(false);  // ← Nuevo: detecta si es primer ingreso
  const navigate = useNavigate();

  // Cargar datos del usuario para saber si es primer ingreso
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userType = localStorage.getItem('userType');
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');

    if (!token || userType !== 'user') {
      toast.error('Debes iniciar sesión como docente');
      navigate('/user-login');
      return;
    }

    // Detectamos si debe cambiar contraseña (primer ingreso)
    if (userData.must_change_password) {
      setIsFirstLogin(true);
      // Opcional: prellenar con "123456" para que sea más fácil
      setFormData(prev => ({ ...prev, current_password: '123456' }));
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validaciones básicas
    if (!formData.new_password || !formData.confirm_password) {
      toast.error('La nueva contraseña y su confirmación son obligatorias');
      return;
    }

    if (formData.new_password !== formData.confirm_password) {
      toast.error('Las nuevas contraseñas no coinciden');
      return;
    }

    if (formData.new_password.length < 6) {
      toast.error('La nueva contraseña debe tener al menos 6 caracteres');
      return;
    }

    // Para primer ingreso, no obligamos a ingresar contraseña actual (pero si la ingresan, el backend lo maneja)
    if (!isFirstLogin && !formData.current_password) {
      toast.error('Ingresa tu contraseña actual');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');

      // Para primer ingreso, enviamos current_password aunque sea "123456" o vacío (el backend lo ignora si must_change_password = true)
      const payload = {
        new_password: formData.new_password,
      };
      if (!isFirstLogin || formData.current_password) {
        payload.current_password = formData.current_password;
      }

      await axios.post(`${API}/user/change-password`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Actualizar localStorage: ya no necesita cambiar contraseña
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      userData.must_change_password = false;
      localStorage.setItem('userData', JSON.stringify(userData));

      toast.success('¡Contraseña cambiada exitosamente!');
      navigate('/user-dashboard');
    } catch (error) {
      console.error('Error al cambiar contraseña:', error);
      const errorMsg =
        error.response?.data?.detail ||
        'No se pudo cambiar la contraseña. Verifica los datos.';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1a4d2e] via-[#2d6a4f] to-[#1a4d2e] flex items-center justify-center p-4">
      <div className="bg-[#f5e6d3]/95 backdrop-blur-md rounded-2xl shadow-2xl p-8 w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <img
            src="/logo_aip.png"
            alt="AIP Logo"
            className="w-24 h-24 object-contain mb-4 rounded-full bg-white p-2 shadow-md"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <h1 className="text-3xl font-bold text-[#1a4d2e] mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Cambiar Contraseña
          </h1>
          <p className="text-[#2d6a4f] text-center text-sm max-w-xs">
            Por seguridad, debes establecer una nueva contraseña personal antes de continuar.
          </p>

          {/* Mensaje especial para primer ingreso */}
          {isFirstLogin && (
            <div className="mt-4 bg-yellow-100 border border-yellow-400 text-yellow-800 px-4 py-3 rounded-lg text-center text-sm">
              <p className="font-medium">¡Es tu primer ingreso!</p>
              <p>La contraseña temporal es <strong>123456</strong>. Ya se ha prellenado por ti, ingresa tu contraseña nueva.</p>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Campo contraseña actual: visible siempre, pero con nota para nuevos */}
          <div>
            <Label htmlFor="current_password" className="text-[#1a4d2e] font-medium">
              Contraseña Actual {isFirstLogin && '(temporal: 123456)'}
            </Label>
            <Input
              id="current_password"
              name="current_password"
              type="password"
              value={formData.current_password}
              onChange={handleChange}
              className="mt-2 bg-white border-[#1a4d2e]/20 focus:border-[#1a4d2e]"
              placeholder={isFirstLogin ? '123456 (ya prellenado)' : 'Ingresa tu contraseña actual'}
              required={!isFirstLogin}  // No obligatorio para primer ingreso
              autoComplete="current-password"
              readOnly={isFirstLogin}  // Solo lectura para que no lo borren accidentalmente
            />
            {!isFirstLogin && (
              <p className="text-xs text-[#2d6a4f] mt-1">Obligatoria si ya cambiaste la contraseña antes</p>
            )}
          </div>

          <div>
            <Label htmlFor="new_password" className="text-[#1a4d2e] font-medium">
              Nueva Contraseña
            </Label>
            <Input
              id="new_password"
              name="new_password"
              type="password"
              value={formData.new_password}
              onChange={handleChange}
              className="mt-2 bg-white border-[#1a4d2e]/20 focus:border-[#1a4d2e]"
              placeholder="Mínimo 6 caracteres"
              required
              autoComplete="new-password"
            />
          </div>

          <div>
            <Label htmlFor="confirm_password" className="text-[#1a4d2e] font-medium">
              Confirmar Nueva Contraseña
            </Label>
            <Input
              id="confirm_password"
              name="confirm_password"
              type="password"
              value={formData.confirm_password}
              onChange={handleChange}
              className="mt-2 bg-white border-[#1a4d2e]/20 focus:border-[#1a4d2e]"
              placeholder="Repite la nueva contraseña"
              required
              autoComplete="new-password"
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-[#1a4d2e] to-[#2d6a4f] text-[#f5e6d3] hover:from-[#2d6a4f] hover:to-[#1a4d2e] transition-all duration-300 font-medium py-6 text-lg"
          >
            {loading ? 'Guardando...' : 'Cambiar Contraseña'}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={() => {
              localStorage.clear();
              setAuth(false);
              setUserType(null);
              navigate('/user-login');
            }}
            className="text-[#1a4d2e] hover:underline text-sm font-medium"
          >
            Cerrar sesión
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;